import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Accordion, Alert, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { FaSave, FaExclamationTriangle } from 'react-icons/fa';
import { patchPuntosCalibracion } from '../../../../api/common';
import { Alertassw } from '../../../Alertas/Alertassw';

/**
 * Modal para ajustar los puntos de calibración por magnitud DESPUÉS de haber
 * creado los lotes con una plantilla. Cubre el caso "elegí la plantilla rápido
 * y olvidé ajustar los puntos".
 *
 * SCOPE INTENCIONADO:
 *   - Solo edita `sesion.puntosCalibracion[]` (los valores de los puntos).
 *   - NO cambia `cantidadPuntos`, `cantMedicionesPorPunto`, `requiereHisteresis`
 *     — esos son estructurales y cambiarlos rompería mediciones ya registradas.
 *   - Si querés esos cambios estructurales, usá "Cambiar plantilla" (swap).
 *
 * PERSISTENCIA: usa `PATCH /lotes-calibracion/:id/sesiones/:idx/actualizar-puntos`
 * per magnitud + per lote. Para lotes bulk se aplica a todos los lotes hijos.
 */
const ModalAjustarPlantillaLote = ({ show, onHide, lotesCalibracion, plantilla, onSaved }) => {
  const [puntosPorMagnitud, setPuntosPorMagnitud] = useState({});
  const [guardando, setGuardando] = useState(false);

  // Init: hidratar puntosPorMagnitud desde el primer lote (todos los lotes hijos
  // comparten la misma config de puntos porque provienen de la misma plantilla).
  useEffect(() => {
    if (!show || !lotesCalibracion || lotesCalibracion.length === 0) return;
    const primerLote = lotesCalibracion[0];
    const inicial = {};
    (primerLote.sesiones || []).forEach((sesion) => {
      inicial[sesion.magnitud] = Array.isArray(sesion.puntosCalibracion)
        ? [...sesion.puntosCalibracion]
        : [];
    });
    setPuntosPorMagnitud(inicial);
  }, [show, lotesCalibracion]);

  const handleChangePunto = (magnitud, idx, valor) => {
    setPuntosPorMagnitud((prev) => {
      const copia = { ...prev, [magnitud]: [...(prev[magnitud] || [])] };
      copia[magnitud][idx] = valor === '' ? '' : Number(valor);
      return copia;
    });
  };

  const handleGuardar = async () => {
    if (!lotesCalibracion || lotesCalibracion.length === 0) return;
    setGuardando(true);
    try {
      const sesiones = lotesCalibracion[0].sesiones || [];
      const promesas = [];
      // Actualizar cada magnitud en cada lote hijo.
      for (const lote of lotesCalibracion) {
        sesiones.forEach((sesion, sesionIndex) => {
          const puntos = puntosPorMagnitud[sesion.magnitud] || [];
          promesas.push(patchPuntosCalibracion(lote._id, sesionIndex, { puntosCalibracion: puntos }));
        });
      }
      await Promise.all(promesas);
      Alertassw('success', 'Puntos actualizados', 'Los puntos de calibración fueron guardados.');
      onSaved?.();
      onHide();
    } catch (err) {
      console.error('Error guardando ajustes de plantilla:', err);
      Alertassw('error', 'Error', err?.response?.data?.message || 'No se pudieron guardar los cambios.');
    } finally {
      setGuardando(false);
    }
  };

  if (!lotesCalibracion || lotesCalibracion.length === 0) return null;
  const sesiones = lotesCalibracion[0].sesiones || [];

  return (
    <Modal show={show} onHide={handleGuardarNo(onHide, guardando)} size="lg" centered scrollable>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>Ajustar plantilla del lote — {plantilla?.codigo || 'plantilla'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <Alert variant="warning" className="d-flex align-items-start">
          <FaExclamationTriangle className="me-2 mt-1" />
          <div>
            <strong>Solo puedes editar los valores de los puntos de calibración.</strong>
            <div className="small mt-1">
              Los cambios estructurales (cantidad de puntos, mediciones por punto,
              histéresis, patrones globales) requieren usar <em>Cambiar plantilla</em>
              o editar la plantilla original desde el módulo de Plantillas.
            </div>
          </div>
        </Alert>

        <Accordion defaultActiveKey="0" alwaysOpen>
          {sesiones.map((sesion, idx) => {
            const puntos = puntosPorMagnitud[sesion.magnitud] || [];
            const cantPuntos = sesion.cantidadPuntos ?? puntos.length;
            return (
              <Accordion.Item key={idx} eventKey={String(idx)}>
                <Accordion.Header>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <span>{sesion.magnitud}</span>
                    <Badge bg="secondary" className="ms-2">
                      {cantPuntos} {cantPuntos === 1 ? 'punto' : 'puntos'}
                    </Badge>
                    {sesion.unidad && <Badge bg="light" text="dark">{sesion.unidad}</Badge>}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row className="g-2">
                    {Array.from({ length: cantPuntos }).map((_, pIdx) => (
                      <Col md={4} sm={6} xs={12} key={pIdx}>
                        <Form.Group>
                          <Form.Label className="small mb-1">
                            Punto {pIdx + 1} {sesion.unidad && <span className="text-muted">({sesion.unidad})</span>}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            step="0.001"
                            size="sm"
                            value={puntos[pIdx] ?? ''}
                            onChange={(e) => handleChangePunto(sesion.magnitud, pIdx, e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="Ej: 20.0"
                            disabled={guardando}
                          />
                        </Form.Group>
                      </Col>
                    ))}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGuardar} disabled={guardando}>
          {guardando ? (
            <><Spinner animation="border" size="sm" className="me-2" /> Guardando…</>
          ) : (
            <><FaSave className="me-2" /> Guardar puntos</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Helper: bloquea el onHide del modal mientras esté guardando para evitar cierre accidental.
const handleGuardarNo = (onHide, guardando) => () => {
  if (guardando) return;
  onHide?.();
};

export default ModalAjustarPlantillaLote;
